import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FiRefreshCw,
  FiArrowLeft,
  FiCalendar,
  FiDollarSign,
  FiUsers,
  FiHome,
  FiClock,
  FiCheckCircle,
  FiAlertTriangle,
  FiXCircle,
  FiPackage,
} from "react-icons/fi";

import "./MySubscription.css";

const url = process.env.REACT_APP_BACKEND_URL;

const getStatusIcon = (status) => {
  switch (status) {
    case "active":
      return <FiCheckCircle />;
    case "trial":
      return <FiClock />;
    case "inactive":
      return <FiAlertTriangle />;
    case "expired":
    case "cancelled":
      return <FiXCircle />;
    default:
      return <FiPackage />;
  }
};

const formatDate = (date) => {
  if (!date) return "Not available";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "Not available";
  }

  return parsedDate.toLocaleDateString();
};

const getRemainingDays = (endDate, status) => {
  if (status === "cancelled") return "Cancelled";
  if (status === "inactive") return "Inactive";
  if (status === "expired") return "Expired";
  if (!endDate) return "Not available";

  const today = new Date();
  const end = new Date(endDate);

  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const difference = Math.ceil(
    (end.getTime() - today.getTime()) /
      (1000 * 60 * 60 * 24)
  );

  if (difference <= 0) return "Expired";

  return `${difference} Day${difference === 1 ? "" : "s"} Left`;
};

export default function MySubscription() {
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState(null);
  const [company, setCompany] = useState(null);
  const [blockedInfo, setBlockedInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadSubscription = useCallback(async (manualRefresh = false) => {
    try {
      if (manualRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");
      setBlockedInfo(null);

      const response = await axios.get(
        `${url}/api/subscriptions/my-subscription`
      );

      if (response.data?.allowed !== false) {
        localStorage.removeItem("subscriptionBlocked");

        window.dispatchEvent(
          new Event("subscription-restored")
        );
      }

      setSubscription(response.data?.subscription || null);
      setCompany(
        response.data?.subscription?.company ||
          response.data?.company ||
          null
      );
    } catch (requestError) {
      const responseData = requestError.response?.data;

      if (responseData?.subscriptionExpired) {
        setBlockedInfo(responseData);
        setSubscription(responseData.subscription || null);
        setCompany(
          responseData.company ||
            responseData.subscription?.company ||
            null
        );
        return;
      }

      setError(
        responseData?.message ||
          "Failed to load subscription details."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const subscriptionStatus =
    subscription?.status ||
    blockedInfo?.subscription?.status ||
    (blockedInfo?.title?.toLowerCase().includes("inactive")
      ? "inactive"
      : blockedInfo?.title?.toLowerCase().includes("cancelled")
      ? "cancelled"
      : blockedInfo?.subscriptionExpired
      ? "expired"
      : "unknown");

  const displaySubscription = useMemo(() => {
    if (subscription) return subscription;
    if (blockedInfo?.subscription) return blockedInfo.subscription;

    return {
      planName: blockedInfo?.planName || "-",
      price: blockedInfo?.amountDue || 0,
      billingCycle: "-",
      startDate: null,
      endDate: blockedInfo?.expiryDate || null,
      maxWarehouses: "-",
      maxUsers: "-",
      features: [],
      notes: "",
      status: subscriptionStatus,
    };
  }, [subscription, blockedInfo, subscriptionStatus]);

  const remaining = getRemainingDays(
    displaySubscription.endDate,
    subscriptionStatus
  );

  const companyName =
    company?.name ||
    company?.company_name ||
    displaySubscription.company?.company_name ||
    displaySubscription.company?.name ||
    "Your Company";

  const companyCode =
    company?.code ||
    company?.company_code ||
    displaySubscription.company?.company_code ||
    "";

  const features = Array.isArray(displaySubscription.features)
    ? displaySubscription.features
    : [];

  if (loading) {
    return (
      <div className="my-subscription-page">
        <div className="subscription-loading-card">
          <div className="subscription-spinner" />
          <p>Loading subscription...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-subscription-page">
      <div className="my-subscription-header">
        <div>
          <h1>My Subscription</h1>
          <p>
            View your current HantiZone IMS subscription,
            limits, dates, and status.
          </p>
        </div>

        <div className="my-subscription-header-actions">
          <button
            type="button"
            className="subscription-btn secondary"
            onClick={() => navigate("/")}
          >
            <FiArrowLeft />
            Dashboard
          </button>

          <button
            type="button"
            className="subscription-btn primary"
            onClick={() => loadSubscription(true)}
            disabled={refreshing}
          >
            <FiRefreshCw className={refreshing ? "spinning" : ""} />
            {refreshing ? "Checking..." : "Check Again"}
          </button>
        </div>
      </div>

      {error && (
        <div className="subscription-alert error">
          <FiAlertTriangle />
          <span>{error}</span>
        </div>
      )}

      {blockedInfo?.subscriptionExpired && (
        <div className="subscription-alert blocked">
          <FiAlertTriangle />
          <div>
            <strong>
              {blockedInfo.title || "Subscription Required"}
            </strong>
            <p>
              {blockedInfo.message ||
                "Your subscription is not active."}
            </p>
            {blockedInfo.details && (
              <small>{blockedInfo.details}</small>
            )}
          </div>
        </div>
      )}

      <section className="subscription-card">
        <div className="subscription-card-header">
          <div>
            <span className="subscription-eyebrow">
              HantiZone IMS
            </span>
            <h2>
              {displaySubscription.planName ||
                "Subscription Plan"}
            </h2>
            <p>
              {companyName}
              {companyCode ? ` • ${companyCode}` : ""}
            </p>
          </div>

          <span
            className={`subscription-badge ${subscriptionStatus}`}
          >
            {getStatusIcon(subscriptionStatus)}
            {subscriptionStatus}
          </span>
        </div>

        <div className="subscription-summary-grid">
          <div className="subscription-summary-box">
            <FiPackage />
            <div>
              <span>Plan</span>
              <strong>{displaySubscription.planName || "-"}</strong>
            </div>
          </div>

          <div className="subscription-summary-box">
            <FiCalendar />
            <div>
              <span>Start Date</span>
              <strong>{formatDate(displaySubscription.startDate)}</strong>
            </div>
          </div>

          <div className="subscription-summary-box">
            <FiCalendar />
            <div>
              <span>Expiry Date</span>
              <strong>{formatDate(displaySubscription.endDate)}</strong>
            </div>
          </div>

          <div className="subscription-summary-box">
            <FiClock />
            <div>
              <span>Remaining</span>
              <strong>{remaining}</strong>
            </div>
          </div>

          <div className="subscription-summary-box">
            <FiDollarSign />
            <div>
              <span>Price</span>
              <strong>
                ${Number(displaySubscription.price || 0).toFixed(2)}
              </strong>
            </div>
          </div>

          <div className="subscription-summary-box">
            <FiClock />
            <div>
              <span>Billing Cycle</span>
              <strong>
                {displaySubscription.billingCycle || "-"}
              </strong>
            </div>
          </div>

          <div className="subscription-summary-box">
            <FiHome />
            <div>
              <span>Max Warehouses</span>
              <strong>
                {displaySubscription.maxWarehouses ?? "-"}
              </strong>
            </div>
          </div>

          <div className="subscription-summary-box">
            <FiUsers />
            <div>
              <span>Max Users</span>
              <strong>{displaySubscription.maxUsers ?? "-"}</strong>
            </div>
          </div>
        </div>

        <div className="subscription-details-grid">
          <div className="subscription-detail-panel">
            <h3>Included Features</h3>

            {features.length > 0 ? (
              <div className="subscription-features">
                {features.map((feature, index) => (
                  <span
                    className="subscription-feature"
                    key={`${feature}-${index}`}
                  >
                    <FiCheckCircle />
                    {feature}
                  </span>
                ))}
              </div>
            ) : (
              <p className="subscription-empty-text">
                No features listed.
              </p>
            )}
          </div>

          <div className="subscription-detail-panel">
            <h3>Notes</h3>
            <p className="subscription-notes">
              {displaySubscription.notes ||
                blockedInfo?.instructions ||
                "No notes available."}
            </p>
          </div>
        </div>
      </section>

      {blockedInfo?.contact && (
        <section className="subscription-contact-card">
          <div>
            <span className="subscription-eyebrow">Assistance</span>
            <h3>Contact HantiZone Accounts</h3>
            <p>
              Contact the accounts department to renew or
              reactivate the subscription.
            </p>
          </div>

          <div className="subscription-contact-grid">
            <div>
              <span>Department</span>
              <strong>
                {blockedInfo.contact.department ||
                  "Accounts Department"}
              </strong>
            </div>

            <div>
              <span>Phone</span>
              <strong>{blockedInfo.contact.phone || "-"}</strong>
            </div>

            <div>
              <span>Email</span>
              <strong>{blockedInfo.contact.email || "-"}</strong>
            </div>
          </div>
        </section>
      )}

      <style>{`
        .spinning {
          animation: subscription-spin 1s linear infinite;
        }

        @keyframes subscription-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}